import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import {
  Brain, UserCircle, Buildings, MagnifyingGlass, LinkedinLogo, ArrowRight, CheckCircle, ClockClockwise, Hourglass, Lock,
} from "@phosphor-icons/react";

// Static catalogue. Only the breintype profile is implemented today; the
// remaining three are placeholders to be designed with the work coach later.
const PROFILE_CATALOG = [
  { id: "breintype",  title: "Mijn breintype",   icon: Brain,           description: "Jouw persoonlijke breintype op basis van het Pad-bij-Aanleg model — sterktes, valkuilen en hoe je het beste tot je recht komt." },
  { id: "werknemer",  title: "Werknemerprofiel", icon: UserCircle,      description: "Wat voor werknemer ben je? Welke rol pak je in een team, welke werkomgeving past bij jou?" },
  { id: "werkgever",  title: "Werkgeverprofiel", icon: Buildings,       description: "Welke type werkgever ben jij op zoek? Cultuur, leiderschap, grootte, branche." },
  { id: "zoek",       title: "Zoekprofiel",      icon: MagnifyingGlass, description: "Het werk dat bij jou past, de regio waarin je wilt werken en de voorwaarden die voor jou belangrijk zijn." },
  { id: "linkedin",   title: "LinkedIn-stappenplan", icon: LinkedinLogo, description: "Een persoonlijk stappenplan om je LinkedIn-profiel te optimaliseren — inclusief kant-en-klare voorbeeldteksten voor je headline, About en meer." },
];

export default function CoacheeProfilesPage() {
  const [progress, setProgress] = useState(null);
  const [searchProfile, setSearchProfile] = useState(null);
  const [employerProfile, setEmployerProfile] = useState(null);
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [linkedinPlan, setLinkedinPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        const [pRes, spRes, epRes, wnRes, liRes] = await Promise.allSettled([
          api.get("/survey/progress"),
          own ? api.get(`/coachees/${own.id}/search-profile`) : Promise.resolve({ data: null }),
          own ? api.get(`/coachees/${own.id}/employer-profile`) : Promise.resolve({ data: null }),
          own ? api.get(`/coachees/${own.id}/employee-profile`) : Promise.resolve({ data: null }),
          own ? api.get(`/coachees/${own.id}/linkedin-plan`) : Promise.resolve({ data: null }),
        ]);
        if (pRes.status === "fulfilled") setProgress(pRes.value.data);
        if (spRes.status === "fulfilled") setSearchProfile(spRes.value.data);
        if (epRes.status === "fulfilled") setEmployerProfile(epRes.value.data);
        if (wnRes.status === "fulfilled") setEmployeeProfile(wnRes.value.data);
        if (liRes.status === "fulfilled") setLinkedinPlan(liRes.value.data);
      } catch (err) {
        console.warn("Profielen overview fetch failed:", err?.message || err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AppLayout title="Profielen">
      <div className="max-w-4xl space-y-6" data-testid="profiles-page">
        <p className="text-sm text-zinc-600 max-w-2xl">
          In jouw profielen leg je vast wie je bent, wat je zoekt en welke werkgever bij jou past.
          Samen met je work coach werk je deze stap voor stap uit.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PROFILE_CATALOG.map((p) => (
            <ProfileCard
              key={p.id}
              profile={p}
              status={
                p.id === "breintype" ? breintypeProfileStatus(progress, loading)
                : p.id === "zoek" ? searchProfileStatus(searchProfile, loading)
                : p.id === "werkgever" ? employerProfileStatus(employerProfile, loading)
                : p.id === "werknemer" ? employeeProfileStatus(employeeProfile, loading)
                : p.id === "linkedin" ? linkedinPlanStatus(linkedinPlan, loading)
                : placeholderStatus()
              }
            />
          ))}
        </div>
      </div>
    </AppLayout>
  );
}

function linkedinPlanStatus(li, loading) {
  if (loading) return { kind: "loading" };
  if (!li || !(li.plan || "").trim()) {
    return { kind: "empty", to: "/profielen/linkedin", action: "Genereer stappenplan" };
  }
  return {
    kind: "complete",
    to: "/profielen/linkedin",
    action: "Bekijk stappenplan",
    label: `Stappenplan klaar (AI: ${li.ai_count || 0}/${li.ai_max || 3})`,
  };
}

function employeeProfileStatus(wn, loading) {
  if (loading) return { kind: "loading" };
  if (!wn) return { kind: "empty", to: "/profielen/werknemer", action: "Vul je werknemerprofiel in" };
  const c = wn.completeness || { filled: 0, total: 0, percent: 0 };
  if (c.percent === 100) {
    return { kind: "complete", to: "/profielen/werknemer", action: "Bekijk werknemerprofiel", label: "Volledig ingevuld" };
  }
  if (c.filled > 0) {
    return {
      kind: "partial",
      to: "/profielen/werknemer",
      action: c.filled <= 2 ? "Vul je werknemerprofiel in" : "Werk je werknemerprofiel bij",
      label: `${c.filled} van ${c.total} secties ingevuld`,
    };
  }
  return { kind: "empty", to: "/profielen/werknemer", action: "Vul je werknemerprofiel in" };
}

function employerProfileStatus(ep, loading) {
  if (loading) return { kind: "loading" };
  if (!ep) return { kind: "empty", to: "/profielen/werkgever", action: "Vul je werkgeversprofiel in" };
  const c = ep.completeness || { filled: 0, total: 0, percent: 0 };
  if (c.percent === 100) {
    return { kind: "complete", to: "/profielen/werkgever", action: "Bekijk werkgeversprofiel", label: "Volledig ingevuld" };
  }
  if (c.filled > 0) {
    return {
      kind: "partial",
      to: "/profielen/werkgever",
      action: c.filled <= 2 ? "Vul je werkgeversprofiel in" : "Werk je werkgeversprofiel bij",
      label: `${c.filled} van ${c.total} secties ingevuld`,
    };
  }
  return { kind: "empty", to: "/profielen/werkgever", action: "Vul je werkgeversprofiel in" };
}

function searchProfileStatus(sp, loading) {
  if (loading) return { kind: "loading" };
  if (!sp) return { kind: "empty", to: "/profielen/zoekprofiel", action: "Vul je zoekprofiel in" };
  const c = sp.completeness || { filled: 0, total: 0, percent: 0 };
  if (c.percent === 100) {
    return {
      kind: "complete",
      to: "/profielen/zoekprofiel",
      action: "Bekijk zoekprofiel",
      label: "Volledig ingevuld",
    };
  }
  if (c.filled > 0) {
    return {
      kind: "partial",
      to: "/profielen/zoekprofiel",
      action: c.filled <= 3 ? "Vul je zoekprofiel in" : "Werk je zoekprofiel bij",
      label: `${c.filled} van ${c.total} velden ingevuld`,
    };
  }
  return { kind: "empty", to: "/profielen/zoekprofiel", action: "Vul je zoekprofiel in" };
}

function breintypeProfileStatus(progress, loading) {
  if (loading) return { kind: "loading" };
  if (!progress) return { kind: "empty", to: "/profielen/breintype", action: "Bekijk profiel" };
  switch (progress.state) {
    case "disabled":
      return { kind: "disabled", label: "Niet beschikbaar" };
    case "not_started":
      return { kind: "empty", to: "/profielen/breintype", action: "Bekijk profiel", label: "Nog leeg — doe eerst de test" };
    case "in_progress":
      return { kind: "in_progress", to: "/profielen/breintype", action: "Bekijk profiel", label: "Test nog niet afgerond" };
    case "awaiting_validation":
      return { kind: "partial", to: "/profielen/breintype", action: "Bekijk voorlopig profiel", label: "Voorlopig — wacht op validatie" };
    case "finalized":
      return { kind: "complete", to: "/profielen/breintype", action: "Bekijk je breintype", label: "Volledig ingevuld" };
    default:
      return { kind: "empty", to: "/profielen/breintype", action: "Bekijk profiel" };
  }
}

function placeholderStatus() {
  return { kind: "placeholder", label: "Binnenkort beschikbaar" };
}

function ProfileCard({ profile, status }) {
  const Icon = profile.icon;
  const isPlaceholder = status.kind === "placeholder" || status.kind === "disabled";
  return (
    <div
      className={
        "neuro-card-bold p-5 bg-white flex flex-col gap-4 transition-colors " +
        (isPlaceholder ? "opacity-70" : "hover:border-zinc-900")
      }
      data-testid={`profile-card-${profile.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex h-11 w-11 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm">
          <Icon size={22} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg font-bold tracking-tight">{profile.title}</h3>
          <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{profile.description}</p>
        </div>
      </div>
      <StatusFooter status={status} profileId={profile.id} />
    </div>
  );
}

function StatusFooter({ status, profileId }) {
  if (status.kind === "loading") {
    return <div className="text-xs text-zinc-400 uppercase tracking-widest">Laden...</div>;
  }
  if (status.kind === "placeholder") {
    return (
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500" data-testid={`profile-status-${profileId}`}>
        <Hourglass size={14} weight="duotone" /> {status.label}
      </div>
    );
  }
  if (status.kind === "disabled") {
    return (
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400" data-testid={`profile-status-${profileId}`}>
        <Lock size={14} weight="duotone" /> {status.label}
      </div>
    );
  }
  const tone =
    status.kind === "complete" ? "text-[#43A047]" :
    status.kind === "partial"  ? "text-amber-600" :
    status.kind === "in_progress" ? "text-[#1E88E5]" :
    "text-zinc-500";
  const StatusIcon =
    status.kind === "complete" ? CheckCircle :
    status.kind === "partial"  ? ClockClockwise :
    status.kind === "in_progress" ? ClockClockwise :
    Hourglass;
  return (
    <div className="space-y-2" data-testid={`profile-status-${profileId}`}>
      {status.label && (
        <div className={"flex items-center gap-2 text-xs uppercase tracking-widest " + tone}>
          <StatusIcon size={14} weight="duotone" /> {status.label}
        </div>
      )}
      <Link
        to={status.to}
        data-testid={`profile-action-${profileId}`}
        className="inline-flex items-center gap-1.5 text-sm font-semibold tracking-wide uppercase text-zinc-900 hover:text-[#1E88E5] transition-colors"
      >
        {status.action} <ArrowRight size={14} />
      </Link>
    </div>
  );
}
